import { useState, useEffect, useMemo } from 'react'
import { useUpdaterChainId } from '~/hooks'
import { useV6SDK } from '~/hooks/useV6SDK'
import { useSpotSwapPair, useSpotSwapUserConfig } from '~/state/dex/hooks'
import useSWR from 'swr'
import { formatUnits } from 'viem'
import { gt, lt } from '~/utils/numbers'
import { getIsETH } from '~/utils/token'
import { WETH } from '~/constants/addresses'

type FetchParams = {
  fromChainId: number
  toChainId: number
  fromToken: string
  toToken: string
}

export const useAmountLimits = (inputAmount) => {
  const sdk = useV6SDK()
  const updaterChainId = useUpdaterChainId()
  const [inputToken, outputToken] = useSpotSwapPair()
  const { isCrossSwap } = useSpotSwapUserConfig()
  const [isAmountUnderMin, setIsAmountUnderMin] = useState(false)
  const [isAmountOverMax, setIsAmountOverMax] = useState(false)
  const [isAmountOutOfRange, setIsAmountOutOfRange] = useState(false)
  const [minDeposit, setMinDeposit] = useState('0')
  const [maxDeposit, setMaxDeposit] = useState('0')

  const fetchKey = useMemo(() => {
    // 判断 inputToken?.chainId === outputToken?.chainId 是因为切换网络时会改变默认币对，但 input 和 output 没有一起同步更新，会出现 new input 和 old output 同时存在的状态。
    const isInvalid =
      !inputToken ||
      !outputToken ||
      inputToken?.chainId !== updaterChainId ||
      !isCrossSwap ||
      inputToken?.chainId === outputToken?.chainId

    if (isInvalid) {
      return null
    }

    const isInputETH = getIsETH(inputToken.address, inputToken.symbol)
    const isOutputETH = getIsETH(outputToken.address, outputToken.symbol)
    const fromToken = isInputETH ? WETH[inputToken.chainId] : inputToken.address
    const toToken = isOutputETH
      ? WETH[outputToken.chainId]
      : outputToken.address

    return [
      'getTradingAmountLimits',
      {
        fromChainId: inputToken.chainId,
        toChainId: outputToken.chainId,
        fromToken,
        toToken,
      },
    ]
  }, [
    isCrossSwap,
    inputToken?.chainId,
    outputToken?.chainId,
    inputToken?.address,
    outputToken?.address,
  ])

  const fetchTradingAmount = async (_key: string, params: FetchParams) => {
    try {
      const {
        minDeposit,
        recommendedDepositInstant,
      } = await sdk.swap.tradingAmountLimits(params)
      return {
        min: minDeposit,
        max: recommendedDepositInstant,
      }
    } catch (error) {
      return null
    }
  }

  const { data } = useSWR(fetchKey, fetchTradingAmount, {
    dedupingInterval: 1000,
  })

  useEffect(() => {
    setMinDeposit('0')
    setMaxDeposit('0')
    setIsAmountUnderMin(false)
    setIsAmountOverMax(false)
    setIsAmountOutOfRange(false)
  }, [
    isCrossSwap,
    inputToken?.chainId,
    outputToken?.chainId,
    inputToken?.address,
    outputToken?.address,
  ])

  useEffect(() => {
    if (inputAmount && data) {
      const { max, min } = data
      const formattedMin = formatUnits(BigInt(min), inputToken.decimals)
      const formattedMax = formatUnits(BigInt(max), inputToken.decimals)
      const isUnderMin = lt(inputAmount, formattedMin)
      const isOverMax = gt(inputAmount, formattedMax)

      setMinDeposit(formattedMin)
      setMaxDeposit(formattedMax)
      setIsAmountUnderMin(isUnderMin)
      setIsAmountOverMax(isOverMax)
      setIsAmountOutOfRange(isUnderMin || isOverMax)
    } else {
      setIsAmountUnderMin(false)
      setIsAmountOverMax(false)
      setIsAmountOutOfRange(false)
    }
  }, [data, inputAmount, inputToken?.decimals])

  return {
    isAmountOutOfRange,
    isAmountUnderMin,
    isAmountOverMax,
    minDeposit,
    maxDeposit,
  }
}
